import { useState, useRef } from "react";
import { Button } from "./ui/button";
import { getAuthToken } from "../lib/auth";
import { Upload, X, Loader2 } from "lucide-react";

interface ImageUploadProps {
  value?: string;
  onChange: (url: string) => void;
  onRemove?: () => void;
  prefix?: string;
}

function getApiBaseUrl(): string {
  if (typeof window !== "undefined" && window.location.hostname === "localhost") {
    return "http://localhost:8787";
  }
  return "https://baby-cafe-guide-api.yamaguchi-c92.workers.dev";
}

export function ImageUpload({ value, onChange, onRemove, prefix = "stores" }: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (file: File) => {
    setError(null);
    setIsUploading(true);

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("認証が必要です");
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("prefix", prefix);

      const response = await fetch(`${getApiBaseUrl()}/api/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "アップロードに失敗しました");
      }

      const data = await response.json();
      onChange(data.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "アップロードに失敗しました");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  if (value) {
    return (
      <div className="relative inline-block">
        <img
          src={value}
          alt="アップロード画像"
          className="h-32 w-32 rounded-lg object-cover border border-stone-200"
        />
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="absolute -top-2 -right-2 rounded-full bg-red-500 p-1 text-white hover:bg-red-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <div
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          flex flex-col items-center justify-center
          h-32 w-32 rounded-lg border-2 border-dashed cursor-pointer
          transition-colors
          ${dragOver ? "border-teal-500 bg-teal-50" : "border-stone-300 hover:border-stone-400 bg-stone-50"}
          ${isUploading ? "pointer-events-none opacity-50" : ""}
        `}
      >
        {isUploading ? (
          <Loader2 className="h-8 w-8 text-stone-400 animate-spin" />
        ) : (
          <>
            <Upload className="h-8 w-8 text-stone-400" />
            <span className="mt-2 text-xs text-stone-500">画像を選択</span>
          </>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileChange}
        className="hidden"
      />
      {error && (
        <p className="mt-2 text-sm text-red-500">{error}</p>
      )}
    </div>
  );
}

interface MultiImageUploadProps {
  images: string[];
  onChange: (images: string[]) => void;
  maxImages?: number;
  prefix?: string;
}

export function MultiImageUpload({ images, onChange, maxImages = 5, prefix = "stores" }: MultiImageUploadProps) {
  const handleAdd = (url: string) => {
    if (images.length < maxImages) {
      onChange([...images, url]);
    }
  };

  const handleRemove = (index: number) => {
    onChange(images.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-wrap gap-3">
      {images.map((url, index) => (
        <ImageUpload
          key={index}
          value={url}
          onChange={() => {}}
          onRemove={() => handleRemove(index)}
          prefix={prefix}
        />
      ))}
      {images.length < maxImages && (
        <ImageUpload
          onChange={handleAdd}
          prefix={prefix}
        />
      )}
    </div>
  );
}
